import Image from "next/image"
import type { ReferencePhoto } from "@/data/grass-reference-photos"

/**
 * Inline species reference photo for a grass guide's "How to Identify" section.
 * Recognition-first ("here's what it looks like"). Renders a photo credit for
 * CC-BY/BY-SA sources and nothing for CC0/public-domain, mirroring the grass-ID
 * result-page credit (components/home/results-display.tsx). The photo + metadata
 * come from data/grass-reference-photos.ts (single source of truth).
 *
 * Aspect ratio is set via inline style (not an arbitrary Tailwind class) to dodge
 * the precompiled-CSS gap; see [[project_tailwind_v4_new_arbitrary_jit_gap]].
 */
export function GrassIdentificationPhoto({
  photo,
  grassName,
}: {
  photo: ReferencePhoto
  grassName: string
}) {
  return (
    <figure className="my-6 max-w-md">
      <div
        className="relative overflow-hidden rounded-xl border border-gray-200 bg-linear-to-br from-gray-100 to-gray-200 shadow-sm"
        style={{ aspectRatio: "3 / 2" }}
      >
        <Image
          src={photo.src}
          alt={`What ${grassName} looks like, a close-up of the blades`}
          fill
          sizes="(max-width: 768px) 100vw, 28rem"
          className="object-cover"
        />
      </div>
      <figcaption className="mt-1.5 text-[11px] font-medium text-gray-500">
        What {grassName} looks like
        {photo.credit && (
          <span className="mt-0.5 block text-[9px] font-normal text-gray-400">
            Photo: {photo.credit}{" "}
            <a
              href={photo.sourceUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="underline hover:text-gray-600"
            >
              {photo.license}
            </a>
          </span>
        )}
      </figcaption>
    </figure>
  )
}
